/**
 * CampaignDetailView - Clean campaign detail page with recipients table and message preview
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Users, XCircle, X, Smartphone, Monitor,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import {
  NotificationCampaign,
  NotificationChannel,
  getCampaigns,
  getChannelLabel,
} from '../../utils/notificationsBroadcastApi';
import { API_BASE_URL } from '../../config/api';

interface CampaignDetailViewProps {
  campaignId: string;
  onBack: () => void;
  showPreviewModal?: boolean;
  setShowPreviewModal?: (show: boolean) => void;
}

// Campaign Send Types (from audit log)
interface CampaignSend {
  id: string;
  pet_owner_id: string;
  pet_owner_name: string | null;
  recipient: string;  // Email or phone used
  status: 'sent' | 'failed';
  error_message: string | null;
  created_at: string;
}

interface CampaignSendsResponse {
  items: CampaignSend[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

// Sample data for preview
const SAMPLE_PREVIEW_DATA: Record<string, string> = {
  // Practice info
  practice_name: 'Happy Paws Veterinary',
  practice_phone: '(555) 123-4567',
  practice_email: 'info@happypawsvet.com',
  practice_address: '123 Main Street, Anytown, CA 94000',
  // Pet info
  pet_name: 'Max',
  pet_names: 'Max',
  owner_pet_name: 'Max',
  // Pet owner info
  pet_owner_name: 'Jane Doe',
  pet_owner_first_name: 'Jane',
  pet_owner_email: 'jane.doe@email.com',
  pet_owner_phone: '(555) 987-6543',
  // Legacy aliases
  name: 'Jane Doe',
  first_name: 'Jane',
  email: 'jane.doe@email.com',
  phone: '(555) 987-6543',
  // Appointment info
  next_appointment_date: 'Friday, January 3rd at 2:00 PM',
  appointment_date: 'Friday, January 3rd at 2:00 PM',
  // Grammar helpers
  'has_have': 'has',
  'is/are': 'is',
  'is_are': 'is',
  // Links
  form_link: 'https://helppet.ai/forms/example',
  book_appointment_link: 'https://helppet.ai/book/example',
};

const applyPlaceholders = (text: string, data: Record<string, string>): string => {
  let result = text;
  for (const [key, value] of Object.entries(data)) {
    result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
  }
  return result;
};

export const CampaignDetailView: React.FC<CampaignDetailViewProps> = ({
  campaignId,
  onBack,
  showPreviewModal: externalShowPreviewModal,
  setShowPreviewModal: externalSetShowPreviewModal,
}) => {
  const [campaign, setCampaign] = useState<NotificationCampaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [localShowPreviewModal, setLocalShowPreviewModal] = useState(false);
  const [viewMode, setViewMode] = useState<'mobile' | 'desktop'>('mobile');
  
  // Use external modal state if provided, otherwise use local state
  const showPreviewModal = externalShowPreviewModal ?? localShowPreviewModal;
  const setShowPreviewModal = externalSetShowPreviewModal ?? setLocalShowPreviewModal;
  
  // Recipients state
  const [recipients, setRecipients] = useState<CampaignSend[]>([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsPage, setRecipientsPage] = useState(1);
  const [recipientsTotalPages, setRecipientsTotalPages] = useState(1);
  const [recipientsTotal, setRecipientsTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<'all' | 'sent' | 'failed'>('all');

  // Load campaign
  useEffect(() => {
    const loadCampaign = async () => {
      try {
        setLoading(true);
        const campaigns = await getCampaigns();
        const found = campaigns.find(c => c.id === campaignId);
        if (found) {
          setCampaign(found);
        } else {
          setError('Campaign not found');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load campaign');
      } finally {
        setLoading(false);
      }
    };
    loadCampaign();
  }, [campaignId]);

  // Load recipients
  useEffect(() => {
    const loadRecipients = async () => {
      if (!campaignId) return;
      
      setRecipientsLoading(true);
      try {
        const token = localStorage.getItem('access_token');
        const params = new URLSearchParams({
          page: recipientsPage.toString(),
          page_size: '25',
        });
        
        // Apply status filter
        if (statusFilter !== 'all') {
          params.append('status_filter', statusFilter);
        }
        
        const response = await fetch(`${API_BASE_URL}/api/v1/notifications/campaigns/${campaignId}/sends?${params}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data: CampaignSendsResponse = await response.json();
          setRecipients(data.items);
          setRecipientsTotalPages(data.total_pages);
          setRecipientsTotal(data.total);
        }
      } catch (err) {
        console.error('Failed to load recipients:', err);
      } finally {
        setRecipientsLoading(false);
      }
    };
    
    loadRecipients();
  }, [campaignId, recipientsPage, statusFilter]);

  // Handle escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (showPreviewModal) {
        setShowPreviewModal(false);
      } else {
        onBack();
      }
    }
  }, [onBack, showPreviewModal]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getRecipientStatusBadge = (entry: CampaignSend) => {
    if (entry.status === 'sent') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
          Sent
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
        Failed
      </span>
    );
  };

  // Preview Modal Component (same as before)
  const PreviewModal = () => {
    if (!campaign) return null;

    const practiceName = campaign.list_name || 'Your Practice';
    const initials = practiceName.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
    const dateStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const subjectLine = campaign.subject || 'Message from ' + practiceName;
    const isEmail = campaign.channel === 'email_plain' || campaign.channel === 'email_html';
    const isSms = campaign.channel === 'sms';

    const IPhoneFrame: React.FC<{ children: React.ReactNode; type: 'sms' | 'email' }> = ({ children, type }) => (
      <div className="relative mx-auto" style={{ width: '280px' }}>
        <div className="relative rounded-[3rem] p-2 shadow-2xl" style={{ backgroundColor: '#1f2937' }}>
          <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-7 rounded-full z-20" style={{ backgroundColor: '#000' }} />
          <div className="relative rounded-[2.5rem] overflow-hidden" style={{ backgroundColor: '#000' }}>
            <div className="h-8" style={{ backgroundColor: '#fff' }} />
            <div className="px-6 pt-1 pb-1 flex items-center justify-between text-xs font-medium" style={{ backgroundColor: '#fff', color: '#111' }}>
              <span className="w-12">9:41</span>
              <div className="flex-1" />
              <div className="flex items-center gap-1 w-14 justify-end">
                <div className="w-6 h-3 border border-current rounded-sm relative">
                  <div className="absolute inset-0.5 rounded-[1px]" style={{ backgroundColor: '#22c55e', width: '70%' }} />
                </div>
              </div>
            </div>
            <div style={{ backgroundColor: '#fff', minHeight: type === 'sms' ? '280px' : '400px', maxHeight: type === 'sms' ? '280px' : '400px', overflowY: 'auto' }}>
              {children}
            </div>
            <div className="px-4 py-4" style={{ backgroundColor: '#fff' }}>
              <div className="w-32 h-1 rounded-full mx-auto" style={{ backgroundColor: '#1f2937' }} />
            </div>
          </div>
        </div>
      </div>
    );

    const SMSContent = () => (
      <div className="p-4 pt-6 h-full" style={{ background: 'linear-gradient(to bottom, #f9fafb, #ffffff)' }}>
        <p className="text-xs text-center mb-4" style={{ color: '#9ca3af' }}>Today</p>
        <div className="flex justify-start mb-2">
          <div className="max-w-[85%]">
            <div className="rounded-2xl rounded-bl-md px-4 py-2.5 text-sm leading-relaxed" style={{ backgroundColor: '#e5e7eb', color: '#111827' }}>
              {applyPlaceholders(campaign.body, SAMPLE_PREVIEW_DATA)}
            </div>
          </div>
        </div>
      </div>
    );

    const MobileEmailContent = () => (
      <div className="h-full flex flex-col" style={{ backgroundColor: '#fff' }}>
        <div className="px-3 py-2" style={{ borderBottom: '1px solid #f3f4f6' }}>
          <h3 className="text-base font-normal truncate" style={{ color: '#111827' }}>{subjectLine}</h3>
        </div>
        <div className="px-3 py-2 text-left" style={{ borderBottom: '1px solid #f3f4f6' }}>
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(to bottom right, #60a5fa, #2563eb)' }}>
              <span className="text-xs font-bold" style={{ color: '#fff' }}>{initials}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-left" style={{ color: '#111827' }}>{practiceName}</p>
              <p className="text-xs text-left" style={{ color: '#6b7280' }}>to me</p>
            </div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-3 text-left" style={{ backgroundColor: '#fff' }}>
          {campaign.channel === 'email_html' ? (
            <div className="overflow-hidden text-left" style={{ transform: 'scale(0.42)', transformOrigin: 'top left', width: '238%', pointerEvents: 'none' }} dangerouslySetInnerHTML={{ __html: campaign.body }} />
          ) : (
            <p className="text-sm leading-relaxed whitespace-pre-wrap text-left" style={{ color: '#374151' }}>{applyPlaceholders(campaign.body, SAMPLE_PREVIEW_DATA)}</p>
          )}
        </div>
      </div>
    );

    const DesktopEmailPreview = () => (
      <div className="w-full max-w-3xl mx-auto rounded-lg shadow-xl overflow-hidden" style={{ backgroundColor: '#fff' }}>
        <div className="px-4 py-3 flex items-center gap-3" style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#ef4444' }} />
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#f59e0b' }} />
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#22c55e' }} />
          </div>
          <div className="flex-1 flex justify-center">
            <div className="px-4 py-1 rounded-md text-xs" style={{ backgroundColor: '#e5e7eb', color: '#6b7280' }}>
              mail.google.com
            </div>
          </div>
        </div>
        <div className="px-6 py-4" style={{ borderBottom: '1px solid #e5e7eb' }}>
          <h2 className="text-xl font-normal mb-3" style={{ color: '#111827' }}>{subjectLine}</h2>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(to bottom right, #60a5fa, #2563eb)' }}>
              <span className="text-sm font-bold" style={{ color: '#fff' }}>{initials}</span>
            </div>
            <div className="flex-1">
              <span className="font-medium" style={{ color: '#111827' }}>{practiceName}</span>
              <p className="text-sm" style={{ color: '#6b7280' }}>to me</p>
            </div>
            <span className="text-sm" style={{ color: '#6b7280' }}>{dateStr}</span>
          </div>
        </div>
        <div className="p-6" style={{ backgroundColor: '#fff', minHeight: '300px', maxHeight: '400px', overflowY: 'auto' }}>
          {campaign.channel === 'email_html' ? (
            <div style={{ pointerEvents: 'none' }} dangerouslySetInnerHTML={{ __html: campaign.body }} />
          ) : (
            <p className="text-base leading-relaxed whitespace-pre-wrap" style={{ color: '#374151' }}>{applyPlaceholders(campaign.body, SAMPLE_PREVIEW_DATA)}</p>
          )}
        </div>
      </div>
    );

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 py-8">
          <div className="fixed inset-0 transition-opacity" style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }} onClick={() => setShowPreviewModal(false)} />
          <div className="relative z-10 w-full max-w-4xl rounded-xl shadow-2xl overflow-hidden bg-white dark:bg-dark-bg-secondary">
            <div className="px-6 py-4 flex items-center justify-between border-b border-gray-200 dark:border-dark-border">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Message Preview</h3>
              <div className="flex items-center gap-1 p-1 rounded-lg bg-gray-100 dark:bg-dark-bg-tertiary">
                <button
                  onClick={() => setViewMode('mobile')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'mobile' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Smartphone className="w-4 h-4" />
                  Mobile
                </button>
                <button
                  onClick={() => setViewMode('desktop')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'desktop' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Monitor className="w-4 h-4" />
                  Desktop
                </button>
              </div>
              <button onClick={() => setShowPreviewModal(false)} className="p-2 rounded-full text-gray-500 hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 bg-gray-50" style={{ minHeight: '500px' }}>
              {viewMode === 'mobile' ? (
                <div className="flex justify-center">
                  {isSms ? (
                    <IPhoneFrame type="sms"><SMSContent /></IPhoneFrame>
                  ) : (
                    <IPhoneFrame type="email"><MobileEmailContent /></IPhoneFrame>
                  )}
                </div>
              ) : (
                isSms ? (
                  <div className="max-w-md mx-auto p-4 rounded-lg shadow-md bg-white">
                    <p className="text-sm leading-relaxed" style={{ color: '#374151' }}>{applyPlaceholders(campaign.body, SAMPLE_PREVIEW_DATA)}</p>
                  </div>
                ) : (
                  <DesktopEmailPreview />
                )
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 dark:bg-dark-bg-tertiary rounded w-1/3" />
          <div className="h-32 bg-gray-200 dark:bg-dark-bg-tertiary rounded" />
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-dark-text-secondary">{error || 'Campaign not found'}</p>
          <button onClick={onBack} className="mt-4 px-4 py-2 text-sm text-blue-600 hover:text-blue-700">
            ← Back to History
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-dark-bg-secondary">
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Stats Grid - Clickable to filter */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <button
            onClick={() => { setStatusFilter('all'); setRecipientsPage(1); }}
            className={`text-left rounded-lg p-4 transition-all ${
              statusFilter === 'all'
                ? 'bg-gray-100 dark:bg-dark-bg-tertiary ring-2 ring-gray-400 dark:ring-gray-500'
                : 'bg-gray-50 dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border hover:ring-2 hover:ring-gray-300'
            }`}
          >
            <div className="text-2xl font-semibold text-gray-900 dark:text-dark-text-primary">
              {campaign.total_recipients}
            </div>
            <div className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">Total Recipients</div>
          </button>
          <button
            onClick={() => { setStatusFilter('sent'); setRecipientsPage(1); }}
            className={`text-left rounded-lg p-4 transition-all ${
              statusFilter === 'sent'
                ? 'bg-green-100 dark:bg-green-900/30 ring-2 ring-green-500'
                : 'bg-gray-50 dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border hover:ring-2 hover:ring-green-300'
            }`}
          >
            <div className="text-2xl font-semibold text-green-600 dark:text-green-400">
              {campaign.sent_count}
            </div>
            <div className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">Successfully Sent</div>
          </button>
          <button
            onClick={() => { setStatusFilter('failed'); setRecipientsPage(1); }}
            className={`text-left rounded-lg p-4 transition-all ${
              statusFilter === 'failed'
                ? 'bg-red-100 dark:bg-red-900/30 ring-2 ring-red-500'
                : 'bg-gray-50 dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border hover:ring-2 hover:ring-red-300'
            }`}
          >
            <div className="text-2xl font-semibold text-red-600 dark:text-red-400">
              {campaign.failed_count}
            </div>
            <div className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">Failed</div>
          </button>
          <div className="text-left rounded-lg p-4 bg-gray-50 dark:bg-dark-bg-tertiary border border-gray-200 dark:border-dark-border">
            <div className="text-2xl font-semibold text-amber-600 dark:text-amber-400">
              {campaign.opted_out_count}
            </div>
            <div className="text-sm text-gray-500 dark:text-dark-text-muted mt-1">Opted Out</div>
          </div>
        </div>

        {/* Campaign Info */}
        <div className="grid grid-cols-4 gap-6 mb-6 pb-6 border-b border-gray-200 dark:border-dark-border">
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-dark-text-muted mb-1">Target List</label>
            <div className="text-gray-900 dark:text-dark-text-primary">
              <span className="font-medium">{campaign.list_name || 'All Customers'}</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-dark-text-muted mb-1">Channel</label>
            <div className="text-gray-900 dark:text-dark-text-primary">
              <span className="font-medium">{getChannelLabel(campaign.channel)}</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-dark-text-muted mb-1">Sent By</label>
            <div className="text-gray-900 dark:text-dark-text-primary">
              <span className="font-medium">{campaign.sent_by_user_name || '—'}</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-500 dark:text-dark-text-muted mb-1">Date</label>
            <div className="text-gray-900 dark:text-dark-text-primary">
              <span className="font-medium">{formatDate(campaign.created_at)}</span>
            </div>
          </div>
        </div>

        {/* Recipients Table */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-4">
            Recipients ({recipientsTotal})
          </h2>

          {recipientsLoading ? (
            <div className="animate-pulse space-y-2">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-12 bg-gray-200 dark:bg-dark-bg-tertiary rounded" />
              ))}
            </div>
          ) : recipients.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 dark:bg-dark-bg-tertiary rounded-lg">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-dark-text-muted">
                {statusFilter === 'all' ? 'No recipients found for this campaign' : `No ${statusFilter} recipients`}
              </p>
            </div>
          ) : (
            <>
              <div className="bg-white dark:bg-dark-bg-secondary border border-gray-200 dark:border-dark-border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-dark-bg-tertiary">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                        Recipient
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                        Status
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                        Error
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 dark:text-dark-text-muted uppercase tracking-wider">
                        Sent At
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
                    {recipients.map((send) => (
                      <tr key={send.id} className="hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary">
                        <td className="px-4 py-3">
                          <span className="font-medium text-gray-900 dark:text-dark-text-primary">
                            {send.pet_owner_name || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-dark-text-secondary">
                          {send.recipient || '—'}
                        </td>
                        <td className="px-4 py-3">
                          {getRecipientStatusBadge(send)}
                        </td>
                        <td className="px-4 py-3">
                          {send.error_message ? (
                            <span className="text-sm text-red-600 dark:text-red-400" title={send.error_message}>
                              {send.error_message.length > 40 
                                ? send.error_message.substring(0, 40) + '...' 
                                : send.error_message}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-dark-text-muted whitespace-nowrap">
                          {formatDate(send.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {recipientsTotalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <div className="text-sm text-gray-500 dark:text-dark-text-muted">
                    Page {recipientsPage} of {recipientsTotalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setRecipientsPage(p => Math.max(1, p - 1))}
                      disabled={recipientsPage === 1}
                      className="p-2 rounded-lg border border-gray-300 dark:border-dark-border disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setRecipientsPage(p => Math.min(recipientsTotalPages, p + 1))}
                      disabled={recipientsPage === recipientsTotalPages}
                      className="p-2 rounded-lg border border-gray-300 dark:border-dark-border disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreviewModal && <PreviewModal />}
    </div>
  );
};

export default CampaignDetailView;
